import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SignedImg } from "@/components/shared/SignedImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ReviewPanel } from "@/components/shared/ReviewPanel";
import { ApproveButton } from "@/components/shared/ApproveButton";
import { celebrate } from "@/lib/celebrate";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { formatHours, formatCZK, formatDate, formatTime } from "@/lib/format";
import { roleLabel } from "@/lib/roles";
import { TimeField } from "@/components/shared/TimeField";
import { Sec, BreakChips } from "@/components/shared/ShiftSections";
import { PresenceMap } from "@/components/shared/PresenceMap";
import { PhotoPinViewer } from "@/components/defects/PhotoPinViewer";
import { DefectTaskForm } from "@/components/defects/DefectTaskForm";
import { shareToWhatsApp } from "@/components/gallery/ShareSheet";
import { useAuth, canLeadTasks } from "@/context/AuthContext";
import {
  Check, Trash2, MoreVertical, Eye, EyeOff,
  Camera, ListChecks, Package, Pencil, MapPin, X,
} from "lucide-react";
import { toast } from "sonner";
import { autoPill } from "@/components/shared/Pill";

const pragueDate = (iso) => iso ? new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Prague", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso)) : "";
const pragueTime = (iso) => iso ? new Intl.DateTimeFormat("cs-CZ", { timeZone: "Europe/Prague", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso)) : "";
const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };

// Štítky varování (161) — pořadí a barvy 1:1 s referencí.
function FlagChips({ flags, mins, distance }) {
  if (!flags || flags.length === 0) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-success bg-success/10 rounded-md px-2 py-0.5" data-testid="flag-none">bez varování</span>;
  }
  const chip = (key, label, cls) => (
    <span key={key} data-testid={`flag-${key}`} className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide rounded-md px-2 py-0.5 ${cls}`}>{label}</span>
  );
  const red = "text-destructive bg-destructive/10";
  const amber = "text-amber-600 dark:text-amber-400 bg-amber-500/10";
  const brand = "text-brand bg-brand/10";
  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.includes("auto_closed") && chip("auto_closed", "auto odhlášeno", red)}
      {flags.includes("no_gps") && chip("no_gps", "bez GPS", amber)}
      {flags.includes("short") && chip("short", `směna ${mins} min`, red)}
      {flags.includes("out_of_radius") && chip("out_of_radius", `mimo okruh stavby · ${distance ?? "?"} m`, amber)}
      {flags.includes("no_photos") && chip("no_photos", "bez fotek", amber)}
      {flags.includes("edited") && chip("edited", "upraveno", brand)}
      {flags.includes("self_declared") && chip("self_declared", "konec doplnil pracovník", brand)}
      {flags.includes("overtime_beyond_auto") && chip("overtime_beyond_auto", "přesčas nad rámec", red)}
    </div>
  );
}

// JEDEN dialog Kontroly směny (161) — schvalování z Přehledu (mode="approve";
// po schválení se okno zavře) i čtení/oprava v Docházce a na kartě zakázky (mode="detail").
// Obsah načítá z /attendance/{sid}/review. 355: na počítači panel zprava (ReviewPanel),
// nav = { index, count, onGo } listuje frontou ke schválení šipkami ↑ ↓.
// 358: v panelu (počítač) Schválit vlevo nahoře jako u úkolu — po schválení panel zůstane, tlačítko zezelená
// a druhé klepnutí schválení vrátí; onApproval(true/false) dá vědět frontě.
export function ShiftReviewDialog({ sid, mode = "approve", canSeeMoney = false, onClose, onChanged, onApproval, nav = null }) {
  const [id, setId] = useState(sid);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ in: "", out: "", brk: "", note: "" });
  const [editing, setEditing] = useState(false);      // detail: odemčení časů
  const [photos, setPhotos] = useState([]);
  const [delPhoto, setDelPhoto] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tab, setTab] = useState("shift");
  // Závady z fotek směny → nový úkol (routes/defects.py, POST /defects/task).
  const { user } = useAuth() || {};   // bez AuthProvideru (testy) = bez závad
  const [drafts, setDrafts] = useState([]);
  const [pinView, setPinView] = useState(null);     // { start, focusDraft }
  const defRef = useRef(null);
  const [defFlash, setDefFlash] = useState(false);
  // Hotovo v prohlížeči (178): zavřít a rovnou ukázat formulář úkolu — dřív se
  // skládal mimo obrazovku a vypadalo to, že se nic nestalo.
  const showDefectForm = () => {
    setPinView(null);
    setTimeout(() => {
      defRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      setDefFlash(true);
      setTimeout(() => setDefFlash(false), 1600);
    }, 260);
  };
  const origRef = useRef({ in: "", out: "", brk: "" });

  useEffect(() => { setId(sid); }, [sid]);

  const loadReview = (rid, quiet = false) => {
    if (!quiet) setLoading(true);
    setMenuOpen(false);
    api.get(`/attendance/${rid}/review`)
      .then((r) => {
        const d = r.data;
        setData(d);
        const init = { in: pragueTime(d.check_in), out: pragueTime(d.check_out), brk: d.break_minutes ?? "", report: d.report || "" };
        origRef.current = init;
        setForm({ ...init, note: "" });
        setPhotos(d.photos || []);
        setEditing(false);
      })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose?.(); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Každá směna začíná čistě — nic z předchozího člověka (161 bug: fotka zůstávala).
    setDelPhoto(null); setConfirmDel(false); setMenuOpen(false); setEditing(false); setTab("shift");
    setDrafts([]); setPinView(null);
    if (!id) { setData(null); setPhotos([]); return; }
    setData(null); setPhotos([]);
    loadReview(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!sid) return null;

  const approved = data?.approval_status === "approved";
  const canEditTimes = mode === "approve" ? !approved : editing;
  const dirty = form.in !== origRef.current.in || form.out !== origRef.current.out || String(form.brk) !== String(origRef.current.brk) || form.report !== origRef.current.report;

  // Živý přepočet hodin a mzdy podle rozepsaných časů.
  const span = (() => { let s = toMin(form.out) - toMin(form.in); if (s < 0) s += 1440; return s; })();
  const brkMin = form.brk === "" ? (span > 360 ? 30 : 0) : Math.max(0, Number(form.brk) || 0);
  const liveHours = Math.max(0, span - brkMin) / 60;
  const rate = data && data.cost != null && data.hours ? data.cost / data.hours : null;
  const liveCost = rate != null ? Math.round(rate * liveHours) : null;

  const buildISO = () => ({
    check_in: `${pragueDate(data.check_in)}T${form.in}`,
    check_out: `${pragueDate(data.check_out) || pragueDate(data.check_in)}T${form.out}`,
  });

  // stay (358, panel vlevo nahoře): bez bubliny a zavření — tlačítko zezelená, vyletí konfety
  const approve = async (e, stay = false) => {
    const from = e?.currentTarget;
    if (dirty && !form.note.trim()) return toast.error("Uveďte důvod úpravy — pracovník ho uvidí.");
    setBusy(true);
    try {
      const { check_in, check_out } = buildISO();
      await api.post(`/attendance/${data.id}/approve`, {
        check_in, check_out, break_minutes: form.brk === "" ? null : Number(form.brk), report: form.report, note: form.note.trim() || undefined,
      });
      onChanged?.(); onApproval?.(true);
      if (stay) { setData((d) => d && { ...d, approval_status: "approved" }); celebrate(from); loadReview(data.id, true); }
      else { toast.success(dirty ? "Upraveno a schváleno." : "Schváleno."); onClose?.(); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // 358: schváleno omylem — druhé klepnutí na „Schváleno" vrátí směnu ke schválení
  const unapprove = async () => {
    setBusy(true);
    try {
      await api.post(`/attendance/${data.id}/unapprove`);
      setData((d) => d && { ...d, approval_status: "pending" }); onChanged?.(); onApproval?.(false); loadReview(data.id, true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveCorrection = async () => {
    if (!form.note.trim()) return toast.error("Uveďte důvod opravy — pracovník ho uvidí.");
    setBusy(true);
    try {
      const { check_in, check_out } = buildISO();
      await api.patch(`/attendance/${data.id}`, {
        check_in, check_out, break_minutes: form.brk === "" ? null : Number(form.brk), report: form.report, note: form.note.trim(),
      });
      toast.success("Záznam opraven."); onChanged?.(); loadReview(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const delRecord = async () => {
    setBusy(true);
    try { await api.delete(`/attendance/${data.id}`); toast.success("Záznam smazán."); onChanged?.(); onClose?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setConfirmDel(false); }
  };

  const toggleEye = async (p) => {
    const to = p.status !== "approved";
    setPhotos((ps) => ps.map((x) => x.id === p.id ? { ...x, status: to ? "approved" : "pending" } : x));
    try { await api.post("/diary/photos/approve", { photo_ids: [p.id], approved: to }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPhotos((ps) => ps.map((x) => x.id === p.id ? { ...x, status: p.status } : x)); }
  };
  const approveAllPhotos = async () => {
    const ids = photos.filter((p) => p.status !== "approved").map((p) => p.id);
    if (!ids.length) return;
    setPhotos((ps) => ps.map((x) => ({ ...x, status: "approved" })));
    try { await api.post("/diary/photos/approve", { photo_ids: ids, approved: true }); toast.success("Fotky přidány do deníku."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadReview(data.id); }
  };
  const doDelPhoto = async () => {
    const p = delPhoto; setDelPhoto(null);
    setPhotos((ps) => ps.filter((x) => x.id !== p.id));
    try { await api.delete(`/photos/${p.id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadReview(data.id); }
  };

  const flagMins = data ? Math.round((data.hours || 0) * 60) : 0;
  const canPinDefects = !!data && canLeadTasks(user) && !!data.project?.id && photos.length > 0;
  const photoUrls = photos.map((p) => p.url);
  const menu = (
    <div className="relative shrink-0">
      <button onClick={() => setMenuOpen((v) => !v)} data-testid="shift-review-menu" className="h-9 w-9 grid place-items-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground"><MoreVertical size={17} /></button>
      {menuOpen && (
        <div className="absolute right-0 mt-1 w-44 rounded-[12px] border border-border bg-popover shadow-lg z-10 p-1" onMouseLeave={() => setMenuOpen(false)}>
          <button onClick={() => { setMenuOpen(false); setConfirmDel(true); }} data-testid="shift-review-delete-menu"
            className="w-full text-left px-3 py-2 rounded-[8px] text-sm font-semibold text-destructive hover:bg-destructive/10 flex items-center gap-2"><Trash2 size={15} /> Smazat záznam</button>
        </div>
      )}
    </div>
  );


  return (
    <>
    <ReviewPanel open={!!sid} onClose={onClose} nav={nav} dirty={canEditTimes && dirty} testid="shift-review-dialog" title="Kontrola směny"
      mobileClass="max-w-3xl [&>*]:min-w-0" onInteractOutside={(e) => { if (delPhoto || confirmDel || pinView) e.preventDefault(); }}>
      {(controls) => (loading || !data ? (
          <div className="p-6 space-y-3" data-testid="shift-review-loading">
            <div className="flex items-start justify-between gap-2"><div className="h-6 w-1/2 rounded bg-muted animate-pulse motion-reduce:animate-none" />{controls}</div>
            <div className="h-24 rounded bg-muted animate-pulse motion-reduce:animate-none" />
          </div>
        ) : (
          <>
            {/* Header — 358: na počítači nahoře řádek Schválit … ⋮ 2 / 5 ↑ ↓ ✕, pod ním jméno */}
            <div className="pl-4 sm:pl-6 pr-14 md:pr-6 pt-1 md:pt-5 pb-3">
              {mode === "approve" && controls && (
                <div className="flex items-center gap-2 mb-3" data-testid="shift-review-top">
                  <ApproveButton label={dirty ? "Upravit a schválit" : "Schválit"} done={approved} disabled={busy}
                    onClick={approved ? unapprove : (e) => approve(e, true)} title={approved ? "Zrušit schválení" : undefined}
                    testid={approved ? "shift-approved" : "shift-approve"} />
                  <span className="flex-1" />
                  {menu}{controls}
                </div>
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl sm:text-2xl font-black tracking-tight truncate" data-testid="shift-review-name">
                    {data.user.name}<span className="text-muted-foreground font-bold text-base"> · {data.user.position || roleLabel(data.user.role)}</span>
                  </h2>
                  <p className="text-sm text-muted-foreground truncate">
                    {data.project.name} · {formatDate(data.date)}{data.rate_kind === "subcontract" ? " · subdodávka, bez mzdy" : ""}
                  </p>
                </div>
                {!(mode === "approve" && controls) && <>{menu}{controls}</>}
              </div>
              <div className="mt-2.5"><FlagChips flags={data.flags} mins={flagMins} distance={data.gps?.distance_m} /></div>
              {/* Směna | Místo */}
              <div ref={autoPill} role="tablist" className="mt-3 -mr-10 md:mr-0 grid grid-cols-2 p-1 rounded-full bg-secondary text-[14px] font-bold" data-testid="shift-tabs">
                {[["shift", "Směna"], ["place", <>Místo{data.gps?.out_of_radius ? <span className="text-amber-600 dark:text-amber-400"> ⚠</span> : null}</>]].map(([k, label]) => (
                  <button key={k} role="tab" type="button" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`shift-tab-${k}`}
                    className={`h-9 rounded-full transition-colors ${tab === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>{label}</button>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto overscroll-contain px-4 sm:px-6 pb-4 space-y-3.5">
              {tab === "place" ? (
                <PresenceMap testid="shift-place" radius={data.gps?.radius_m || 300}
                  sites={[{ ...(data.gps?.site || {}), label: data.project.name }]}
                  points={[
                    { key: "in", letter: "P", color: "#1f8a5b", label: "Příchod", time: form.in || pragueTime(data.check_in),
                      lat: data.gps?.checkin?.lat, lng: data.gps?.checkin?.lng, distance_m: data.gps?.checkin?.distance_m, out: data.gps?.checkin?.out_of_radius,
                      note: data.manual ? "ručně zadáno" : null },
                    { key: "out", letter: "O", color: "#c77a12", label: "Odchod", time: form.out || pragueTime(data.check_out),
                      lat: data.gps?.checkout?.lat, lng: data.gps?.checkout?.lng, distance_m: data.gps?.checkout?.distance_m, out: data.gps?.checkout?.out_of_radius,
                      note: data.auto_closed_at ? "auto odhlášeno" : null },
                  ]} />
              ) : (<>
              {/* Čas + přestávka + hodiny */}
              <section className="rounded-[18px] border border-border bg-secondary/40 p-3.5 space-y-3 [&_input]:bg-card" data-testid="shift-sec-time">
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="min-w-0">
                    <Label className="text-xs text-muted-foreground">Příchod{canEditTimes && form.in !== origRef.current.in ? " · upraveno" : ""}</Label>
                    {canEditTimes
                      ? <div className={`mt-1 rounded-[12px] ${form.in !== origRef.current.in ? "ring-2 ring-brand/40" : ""}`}><TimeField value={form.in} onChange={(v) => setForm((f) => ({ ...f, in: v }))} testid="shift-in" label="Příchod" /></div>
                      : <div className="h-11 mt-1 grid items-center font-bold text-lg tabular-nums" data-testid="shift-in-read">{form.in}</div>}
                  </div>
                  <div className="min-w-0">
                    <Label className="text-xs text-muted-foreground">Odchod{canEditTimes && form.out !== origRef.current.out ? " · upraveno" : ""}</Label>
                    {canEditTimes
                      ? <div className={`mt-1 rounded-[12px] ${form.out !== origRef.current.out ? "ring-2 ring-brand/40" : ""}`}><TimeField value={form.out} onChange={(v) => setForm((f) => ({ ...f, out: v }))} testid="shift-out" label="Odchod" /></div>
                      : <div className="h-11 mt-1 grid items-center font-bold text-lg tabular-nums" data-testid="shift-out-read">{form.out}</div>}
                  </div>
                </div>
                <div data-testid="shift-sec-break">
                  {canEditTimes
                    ? <BreakChips value={form.brk} onChange={(v) => setForm((f) => ({ ...f, brk: v }))} withAuto autoMinutes={span > 360 ? 30 : 0} testid="shift-break" />
                    : <div className="text-sm" data-testid="shift-break-read">Přestávka <b className="tabular-nums">{form.brk === "" ? `auto (${span > 360 ? 30 : 0} min)` : `${form.brk} min`}</b></div>}
                </div>
                <div className="flex items-baseline justify-between gap-3" data-testid="shift-hours">
                  <span className="text-[13px] text-muted-foreground min-w-0 truncate">
                    Odpracováno{canSeeMoney && data.cost != null ? <> · {formatCZK(liveCost)}</> : null}{dirty && Math.abs(liveHours - (data.hours || 0)) > 0.001 ? <> · bylo {formatHours(data.hours)}</> : null}
                  </span>
                  <b className="text-2xl font-black tracking-tight tabular-nums whitespace-nowrap">{formatHours(liveHours)}</b>
                </div>
                {data.auto_closed_at && <p className="text-[13px] text-destructive font-semibold">Odhlásil systém ve {pragueTime(data.auto_closed_at)}</p>}
              </section>

              {canEditTimes && dirty && (
                <div className="rounded-[14px] border-[1.5px] border-amber-500/70 bg-amber-500/[0.07] p-3" data-testid="shift-sec-reason">
                  <Label className="text-[13px] font-bold">Důvod úpravy <span className="text-destructive">*</span></Label>
                  <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="Např. pozdní příchod, zapomenuté odhlášení…" className="h-11 mt-1.5 w-full min-w-0 bg-card" data-testid="shift-reason" />
                  <p className="text-[12px] text-muted-foreground mt-1.5">Pracovník důvod uvidí.</p>
                </div>
              )}

              {/* Na čem dělal */}
              <Sec icon={Pencil} title="Na čem dělal" testid="shift-sec-report">
                {canEditTimes ? (
                  <Textarea rows={3} value={form.report} onChange={(e) => setForm((f) => ({ ...f, report: e.target.value }))}
                    placeholder="Např. betonáž desky, montáž bednění…" className="w-full min-w-0" data-testid="shift-report-edit" />
                ) : (
                  <div className="bg-card border border-border rounded-[12px] p-3.5 text-sm leading-relaxed break-words" data-testid="shift-report">
                    {data.report?.trim() ? data.report : <span className="italic text-muted-foreground">Bez popisu.</span>}
                  </div>
                )}
              </Sec>
              {/* Úkoly */}
              {(data.tasks_completed.length > 0 || data.tasks_worked.length > 0) && (
                <Sec icon={ListChecks} title="Úkoly">
                  <div className="space-y-1.5" data-testid="shift-tasks">
                    {data.tasks_completed.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 text-sm"><span className="text-success"><Check size={15} /></span><span className="truncate">{t.title}</span><span className="text-[11px] font-bold uppercase text-success bg-success/10 rounded-md px-1.5 py-0.5">dokončeno</span></div>
                    ))}
                    {data.tasks_worked.filter((t) => !data.tasks_completed.some((c) => c.id === t.id)).map((t) => (
                      <div key={t.id} className="flex items-center gap-2 text-sm text-muted-foreground"><span className="w-[15px]" /><span className="truncate">{t.title}</span><span className="text-[11px] font-bold uppercase bg-card rounded-md px-1.5 py-0.5">pracoval</span></div>
                    ))}
                  </div>
                </Sec>
              )}

              {/* Materiál */}
              {data.materials.length > 0 && (
                <Sec icon={Package} title="Nahlášený materiál">
                  <div className="space-y-1" data-testid="shift-materials">
                    {data.materials.map((m, i) => (
                      <div key={i} className="flex justify-between gap-3 text-sm"><span className="truncate">{m.name}</span><span className="tabular-nums shrink-0">{m.qty} {m.unit}</span></div>
                    ))}
                  </div>
                </Sec>
              )}

              {/* Fotky */}
              <Sec icon={Camera} testid="shift-sec-photos"
                title={<>Fotky · {photos.length}{data.photos_required && photos.length === 0 && <span className="ml-1.5 text-[11px] font-bold normal-case tracking-normal text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-md px-2 py-0.5" data-testid="shift-photos-missing">povinné, chybí</span>}</>}
                action={(canPinDefects || photos.some((p) => p.status !== "approved")) && (
                  <span className="flex items-center gap-3 shrink-0">
                    {canPinDefects && <button onClick={() => setPinView({ start: 0 })} data-testid="shift-defects-pin" className="inline-flex items-center gap-1 text-[13px] font-bold text-brand hover:underline"><MapPin size={14} /> Závada</button>}
                    {photos.some((p) => p.status !== "approved") && <button onClick={approveAllPhotos} data-testid="shift-photos-approve-all" className="text-[13px] font-bold text-brand hover:underline">Vše do deníku</button>}
                  </span>
                )}>
                {photos.length ? (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2" data-testid="shift-photos">
                    {photos.map((p) => {
                      const inDiary = p.status === "approved";
                      return (
                        <div key={p.id} data-testid={`shift-photo-${p.id}`} className="relative aspect-square rounded-[12px] overflow-hidden border border-border">
                          <button type="button" onClick={() => setPinView({ start: photos.indexOf(p) })} className="w-full h-full" aria-label="Zvětšit fotku"><SignedImg url={p.url} alt="" thumb={200} className="w-full h-full object-cover" /></button>
                          <div className="absolute top-1.5 right-1.5 flex gap-1">
                            <button onClick={() => toggleEye(p)} data-testid={`shift-photo-eye-${p.id}`} title={inDiary ? "V deníku" : "Skryto z deníku"}
                              className={`w-8 h-8 rounded-full grid place-items-center ${inDiary ? "bg-success text-white" : "bg-black/45 text-white/85"}`}>{inDiary ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                            <button onClick={() => setDelPhoto(p)} data-testid={`shift-photo-del-${p.id}`} title="Smazat fotku"
                              className="w-8 h-8 rounded-full grid place-items-center bg-black/45 text-white/85 hover:bg-destructive"><Trash2 size={14} /></button>
                          </div>
                          <span className="absolute bottom-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: inDiary ? "rgba(34,140,79,.9)" : "rgba(40,40,44,.85)", color: "#fff" }}>{inDiary ? "v deníku" : "skrytá"}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : <p className="text-sm text-muted-foreground">Žádné fotky.</p>}
              </Sec>

              <DefectTaskForm ref={defRef} drafts={drafts} setDrafts={setDrafts} highlight={defFlash}
                projectId={data.project?.id} projectName={data.project?.name} assignee={data.user} sourceKind="attendance" sourceId={data.id}
                onEditDraft={(k) => setPinView({ start: Math.max(0, photoUrls.indexOf(drafts[k].photo_url)), focusDraft: k })}
                note="Docházku schválíte zvlášť. Úkol dostane fotky se špendlíky a popisy." />

              {/* Historie */}
              <section className="border-t border-dashed border-border pt-3 text-[13px] text-muted-foreground space-y-1" data-testid="shift-history">
                <div>Příchod: {data.manual ? "ručně zadáno" : "GPS z mobilu"} · {pragueTime(data.check_in)}</div>
                <div>Odchod: {data.auto_closed_at ? `systém ve ${pragueTime(data.auto_closed_at)}` : (data.check_out ? pragueTime(data.check_out) : "—")}</div>
                {(data.corrections || []).map((c, i) => (
                  <div key={i}>upraveno {c.user_name || c.by_name || "?"}{c.at ? `, ${formatDate(c.at)}` : ""}{c.after && Object.prototype.hasOwnProperty.call(c.after, "report") ? " · popis práce upraven" : ""}{c.note ? `: ${c.note}` : ""}</div>
                ))}
                {approved && data.approved_by_name && <div className="text-success font-semibold">Schváleno ✓ {data.approved_by_name}{data.approved_at ? `, ${formatDate(data.approved_at)}` : ""}</div>}
              </section>
              </>)}
            </div>
            {/* Footer — při schvalování v panelu není potřeba (Schválit je nahoře) */}
            {!(mode === "approve" && controls) && <div className="border-t border-border bg-card px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-col-reverse md:flex-row md:items-center gap-2">
              {mode === "approve" ? (
                <>
                  <div className="hidden md:block flex-1" />
                  {approved ? (
                    <Button disabled className="h-[52px] md:h-12 w-full md:w-auto rounded-full font-bold px-6" data-testid="shift-approved"><Check size={18} /> Schváleno ✓</Button>
                  ) : (
                    <Button onClick={approve} disabled={busy} data-testid="shift-approve"
                      className="h-[52px] md:h-12 w-full md:w-auto rounded-full font-extrabold text-[15px] px-8 bg-success hover:bg-success/90 text-white">
                      <Check size={18} /> {dirty ? "Upravit a schválit" : "Schválit"}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setConfirmDel(true)} disabled={busy} data-testid="shift-delete"
                    className="h-12 w-full md:w-auto rounded-full font-bold text-destructive hover:text-destructive"><Trash2 size={16} /> Smazat záznam</Button>
                  <div className="hidden md:block flex-1" />
                  {editing ? (
                    <Button onClick={saveCorrection} disabled={busy} data-testid="shift-save" className="h-[52px] md:h-12 w-full md:w-auto rounded-full font-extrabold px-8"><Check size={17} /> Uložit opravu</Button>
                  ) : (
                    <Button onClick={() => setEditing(true)} data-testid="shift-edit" className="h-[52px] md:h-12 w-full md:w-auto rounded-full font-extrabold px-8"><Pencil size={16} /> Opravit záznam</Button>
                  )}
                </>
              )}
            </div>}
          </>
        ))}
    </ReviewPanel>

    <AlertDialog open={!!delPhoto} onOpenChange={(o) => { if (!o) setDelPhoto(null); }}>
      <AlertDialogContent data-testid="shift-del-photo-dialog">
        <AlertDialogHeader><AlertDialogTitle>Smazat fotku?</AlertDialogTitle><AlertDialogDescription>Zmizí i z galerie.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => setDelPhoto(null)} className="h-11 w-full sm:w-auto">Zpět</Button>
          <Button variant="destructive" onClick={doDelPhoto} className="h-11 w-full sm:w-auto font-bold" data-testid="shift-del-photo-confirm">Smazat</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(false); }}>
      <AlertDialogContent data-testid="shift-del-record-dialog">
        <AlertDialogHeader><AlertDialogTitle>Smazat záznam docházky?</AlertDialogTitle><AlertDialogDescription>Tato akce je nevratná.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => setConfirmDel(false)} className="h-11 w-full sm:w-auto">Zpět</Button>
          <Button variant="destructive" onClick={delRecord} disabled={busy} className="h-11 w-full sm:w-auto font-bold" data-testid="shift-del-record-confirm">Smazat záznam</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <PhotoPinViewer open={!!pinView} onClose={() => setPinView(null)} photos={photoUrls} start={pinView?.start || 0}
      focusDraft={pinView?.focusDraft ?? null} title={data ? `${data.user.name} · ${data.project.name}` : ""}
      drafts={drafts} setDrafts={setDrafts} editable={canPinDefects} onDone={showDefectForm}
      info={(url) => { const p = photos.find((x) => x.url === url); return p ? { author: p.author_name || data?.user?.name, time: p.created_at ? formatTime(p.created_at) : "", src: "docházka" } : null; }}
      diary={{ isIn: (url) => photos.find((x) => x.url === url)?.status === "approved", toggle: (url) => { const p = photos.find((x) => x.url === url); if (p) toggleEye(p); } }}
      onShare={canLeadTasks(user) ? (url) => { const p = photos.find((x) => x.url === url); if (p) shareToWhatsApp([p.id]); } : undefined}
      onDelete={(url) => { const p = photos.find((x) => x.url === url); if (p) { setPhotos((ps) => ps.filter((x) => x.id !== p.id)); api.delete(`/photos/${p.id}`).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); loadReview(data.id); }); } }} />
    </>
  );
}
